// Client-side PDF text extraction (pdf.js), shared by the Lab Triage screen and the Health Records
// auto-triage hook.
//
// pdf.js is bundled with the app (npm `pdfjs-dist`, version pinned in package.json) instead of being
// injected from a CDN: no third-party script at runtime, nothing to pin with SRI, and the CSP no longer
// needs to allow a third-party CDN for scripts or workers.
//
// The text is rebuilt into real LINES. pdf.js returns loose text items; joining a whole page with ' '
// (what both screens used to do) produced ONE line per page, which put table rows on top of each other and
// stopped the backend's line-anchored patterns (patient-name / identity extraction and PII redaction) from
// finding header fields. Items on the same baseline become one line, with a two-space gap between table
// columns — the "2+ spaces" field boundary those backend patterns already expect.
import { itemsToText } from './pdfTextLines';

let pdfjsPromise = null;
const loadPdfJs = () => {
  if (!pdfjsPromise) {
    pdfjsPromise = import('pdfjs-dist/legacy/build/pdf').then((lib) => {
      // Worker is emitted by webpack as a same-origin asset (covered by CSP worker-src 'self').
      lib.GlobalWorkerOptions.workerSrc =
        new URL('pdfjs-dist/legacy/build/pdf.worker.min.js', import.meta.url).toString();
      return lib;
    }).catch((err) => {
      pdfjsPromise = null; // allow a retry on the next upload
      throw err;
    });
  }
  return pdfjsPromise;
};

/**
 * Extracts a PDF's text layer in the browser. Resolves to '' for image-only (scanned) PDFs.
 * `maxPages` reads only the first N pages (the upload page's date suggestion); the default is every page.
 */
export const extractPdfText = async (file, { maxPages } = {}) => {
  const lib = await loadPdfJs();
  const pdf = await lib.getDocument({ data: new Uint8Array(await file.arrayBuffer()) }).promise;
  try {
    const pages = [];
    const last = maxPages > 0 ? Math.min(pdf.numPages, maxPages) : pdf.numPages;
    for (let i = 1; i <= last; i += 1) {
      const page = await pdf.getPage(i);
      const content = await page.getTextContent();
      pages.push(itemsToText(content.items));
    }
    return pages.join('\n').trim();
  } finally {
    pdf.destroy();
  }
};
